const DEFAULT_BASE_SCREENS = 1
const DEFAULT_MAX_SCREENS = 12
const DEFAULT_LOAD_LATENCY_MS = 700
const DEFAULT_RENDER_BUDGET_MS = 100
const DEFAULT_SAFETY_FACTOR = 1.35
const DEFAULT_LOADED_WINDOWS = 4

const finiteNonNegative = (value, fallback = 0) => {
  const number = Number(value)
  return Number.isFinite(number) && number >= 0 ? number : fallback
}

const adaptiveEdgeLookaheadPx = ({
  clientHeight,
  velocityPxPerMs,
  loadLatencyMs,
  baseScreens = DEFAULT_BASE_SCREENS,
  maxScreens = DEFAULT_MAX_SCREENS,
  renderBudgetMs = DEFAULT_RENDER_BUDGET_MS,
  safetyFactor = DEFAULT_SAFETY_FACTOR
} = {}) => {
  const height = finiteNonNegative(clientHeight)
  if (height === 0) return 0

  const base = height * finiteNonNegative(baseScreens, DEFAULT_BASE_SCREENS)
  const maximum = Math.max(
    base,
    height * finiteNonNegative(maxScreens, DEFAULT_MAX_SCREENS)
  )
  const velocity = finiteNonNegative(velocityPxPerMs)
  const latency = finiteNonNegative(loadLatencyMs, DEFAULT_LOAD_LATENCY_MS)
  const renderBudget = finiteNonNegative(renderBudgetMs, DEFAULT_RENDER_BUDGET_MS)
  const safety = finiteNonNegative(safetyFactor, DEFAULT_SAFETY_FACTOR)
  const travelDuringLoad = velocity * (latency + renderBudget) * safety

  return Math.min(maximum, Math.max(base, base + travelDuringLoad))
}

const smoothedScrollVelocity = ({
  previousVelocity = 0,
  deltaPx,
  elapsedMs,
  smoothing = 0.35,
  resetAfterMs = 250
} = {}) => {
  const elapsed = Number(elapsedMs)
  const delta = Math.abs(Number(deltaPx) || 0)
  if (!Number.isFinite(elapsed) || elapsed <= 0 || delta === 0) {
    return finiteNonNegative(previousVelocity)
  }

  const instantaneous = delta / elapsed
  if (elapsed >= resetAfterMs) return instantaneous

  const weight = Math.min(1, Math.max(0, Number(smoothing) || 0))
  return finiteNonNegative(previousVelocity) * (1 - weight) + instantaneous * weight
}

const adaptiveLoadedItemLookahead = ({
  lookaheadPx,
  renderedPixelHeight,
  renderedItemCount,
  windowSize,
  minimumWindows = DEFAULT_LOADED_WINDOWS
} = {}) => {
  const size = Math.max(1, Math.ceil(finiteNonNegative(windowSize, 1)))
  const minimum = Math.max(
    size,
    Math.ceil(size * finiteNonNegative(minimumWindows, DEFAULT_LOADED_WINDOWS))
  )
  const count = Math.max(0, Math.floor(finiteNonNegative(renderedItemCount)))
  const height = finiteNonNegative(renderedPixelHeight)
  if (!count || !height) return minimum
  const averageItemHeight = height / count
  const pixelItems = Math.ceil(
    finiteNonNegative(lookaheadPx) / Math.max(1, averageItemHeight)
  )
  return Math.max(minimum, pixelItems)
}

module.exports = {
  DEFAULT_LOAD_LATENCY_MS,
  adaptiveLoadedItemLookahead,
  adaptiveEdgeLookaheadPx,
  smoothedScrollVelocity
}
